import { useCallback } from 'react';
import { normalizeFieldValue } from '../regexHandler';

/**
 * Tüm modal formları için ortak input handler hook'u.
 *
 * - text/select/textarea → normalizeFieldValue(name, value) ile normalize eder
 * - checkbox → checked değerini kullanır
 * - radio → value'yu doğrudan kullanır (boolean / string)
 * - Her değişiklikte ilgili field'ın validation hatasını temizler
 *
 * @param {Function} setFormData — formData state setter (prev => next)
 * @param {Function} setErrors   — errors state setter   (prev => next)
 * @param {Object}   [options]
 * @param {Object}   [options.customHandlers] — { fieldName: (value, prev) => newFormData } field-specific override
 * @returns {Function} handleInputChange(e)
 */
export const useFormInputHandler = (setFormData, setErrors, options = {}) => {
    const { customHandlers } = options;

    return useCallback((e) => {
        const { name, value, type, checked } = e.target;

        // Custom handler varsa, önce onu çalıştır
        if (customHandlers && customHandlers[name]) {
            setFormData((prev) => {
                const result = customHandlers[name](type === 'checkbox' ? checked : value, prev);
                return typeof result === 'object' ? result : prev;
            });

            if (setErrors) {
                setErrors((prev) => (prev[name] ? { ...prev, [name]: null } : prev));
            }
            return;
        }

        let processedValue;

        if (type === 'checkbox') {
            processedValue = checked;
        } else if (type === 'radio') {
            // Radio value'lar boolean veya string olabilir — doğrudan kullan
            processedValue = value;
        } else {
            // Tüm text/select/textarea alanları normalizeFieldValue'dan geçir
            processedValue = normalizeFieldValue(name, value);
        }

        setFormData((prev) => ({ ...prev, [name]: processedValue }));

        // İlgili field'ın hatasını temizle
        if (setErrors) {
            setErrors((prev) => (prev[name] ? { ...prev, [name]: null } : prev));
        }
    }, [setFormData, setErrors, customHandlers]);
};
